import { supabase } from "../supabaseClient";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Check if a user has a specific permission for a tournament
 * @param {string} userId - The user's ID
 * @param {string} tournamentId - The tournament's ID
 * @param {string} permission - The permission to check
 * @returns {Promise<{hasPermission: boolean, userRole: string, error: string}>}
 */
export const checkTournamentPermission = async (
  userId,
  tournamentId,
  permission
) => {
  try {
    // First, get the tournament to find its organization
    const { data: tournament, error: tournamentError } = await supabase
      .from("tournaments")
      .select("organizationID")
      .eq("id", tournamentId)
      .maybeSingle();

    if (tournamentError) {
      return {
        hasPermission: false,
        userRole: null,
        error: "Tournament not found",
      };
    }

    // Get the user's membership in the organization
    const { data: membership, error: membershipError } = await supabase
      .from("organizationMembers")
      .select("id, organizationID, status")
      .eq("userID", userId)
      .eq("organizationID", tournament.organizationID)
      .eq("status", "active")
      .maybeSingle();

    if (membershipError || !membership) {
      return {
        hasPermission: false,
        userRole: null,
        error: "User is not a member of this organization",
      };
    }

    // Get the user's roles in the organization using the membership ID
    const { data: memberRoles, error: memberError } = await supabase
      .from("organizationMemberRoles")
      .select(
        `
        roleID,
        role:organizationRoles (
          id,
          name,
          slug,
          permissions,
          rank
        )
      `
      )
      .eq("memberID", membership.id)
      .eq("isDeleted", false);

    if (memberError) {
      return {
        hasPermission: false,
        userRole: null,
        error: "Error fetching user roles",
      };
    }

    // Check if user has any roles with the required permission
    let hasPermission = false;
    let highestRole = null;
    let highestRank = 0;

    for (const memberRole of memberRoles) {
      const role = memberRole.role;
      if (!role) continue;

      // Parse permissions JSON
      const permissions =
        typeof role.permissions === "string"
          ? JSON.parse(role.permissions)
          : role.permissions;

      // Check if this role has the required permission
      if (permissions[permission]) {
        hasPermission = true;

        // Track the highest ranking role for context
        if (role.rank > highestRank) {
          highestRank = role.rank;
          highestRole = role;
        }
      }
    }

    return {
      hasPermission,
      userRole: highestRole?.slug || null,
      error: null,
    };
  } catch (error) {
    console.error("Error checking tournament permission:", error);
    return {
      hasPermission: false,
      userRole: null,
      error: "Error checking permissions",
    };
  }
};

/**
 * Check if a user can manage a specific tournament
 * @param {string} userId - The user's ID
 * @param {string} tournamentId - The tournament's ID
 * @returns {Promise<{canManage: boolean, userRole: string, error: string}>}
 */
export const canManageTournament = async (userId, tournamentId) => {
  const result = await checkTournamentPermission(
    userId,
    tournamentId,
    "manage_tournaments"
  );
  return {
    canManage: result.hasPermission,
    userRole: result.userRole,
    error: result.error,
  };
};

/**
 * Check if a user can enter scores for a tournament
 * @param {string} userId - The user's ID
 * @param {string} tournamentId - The tournament's ID
 * @returns {Promise<{canEnterScores: boolean, userRole: string, error: string}>}
 */
export const canEnterScores = async (userId, tournamentId) => {
  const result = await checkTournamentPermission(
    userId,
    tournamentId,
    "enter_scores"
  );
  return {
    canEnterScores: result.hasPermission,
    userRole: result.userRole,
    error: result.error,
  };
};

/**
 * Check if a user can access bowler contact information for an organization
 * @param {string} userId - The user's ID
 * @param {string} organizationId - The organization's ID
 * @returns {Promise<{canAccessContactInfo: boolean, userRole: string, error: string}>}
 */
export const canAccessBowlerContactInfo = async (userId, organizationId) => {
  try {
    // Get the user's membership in the organization
    const { data: membership, error: membershipError } = await supabase
      .from("organizationMembers")
      .select("id, organizationID, status")
      .eq("userID", userId)
      .eq("organizationID", organizationId)
      .eq("status", "active")
      .maybeSingle();

    if (membershipError || !membership) {
      return {
        canAccessContactInfo: false,
        userRole: null,
        error: "User is not a member of this organization",
      };
    }

    // Get the user's roles in the organization using the membership ID
    const { data: memberRoles, error: memberError } = await supabase
      .from("organizationMemberRoles")
      .select(
        `
        roleID,
        role:organizationRoles (
          id,
          name,
          slug,
          permissions,
          rank
        )
      `
      )
      .eq("memberID", membership.id)
      .eq("isDeleted", false);

    if (memberError) {
      return {
        canAccessContactInfo: false,
        userRole: null,
        error: "Error fetching user roles",
      };
    }

    // Check if user has any roles with the required permission
    let canAccessContactInfo = false;
    let highestRole = null;
    let highestRank = 0;

    for (const memberRole of memberRoles) {
      const role = memberRole.role;
      if (!role) continue;

      // Parse permissions JSON
      const permissions =
        typeof role.permissions === "string"
          ? JSON.parse(role.permissions)
          : role.permissions;

      // Check if this role has the required permission
      if (
        permissions["access_bowler_contact_info"] ||
        permissions["manage_bowlers"] ||
        permissions["manage_organization_bowlers"]
      ) {
        canAccessContactInfo = true;

        // Track the highest ranking role for context
        if (role.rank > highestRank) {
          highestRank = role.rank;
          highestRole = role;
        }
      }
    }

    return {
      canAccessContactInfo,
      userRole: highestRole?.slug || null,
      error: null,
    };
  } catch (error) {
    console.error("Error checking bowler contact info permission:", error);
    return {
      canAccessContactInfo: false,
      userRole: null,
      error: "Error checking permissions",
    };
  }
};

/**
 * React hook for tournament authorization
 * @param {string} tournamentId - The tournament's ID
 * @returns {Object} Authorization state and functions
 */
export const useTournamentAuth = (tournamentId) => {
  const [authState, setAuthState] = useState({
    canManage: false,
    canEnterScores: false,
    userRole: null,
    loading: true,
    error: null,
  });

  const { user } = useAuth();

  useEffect(() => {
    const checkAuth = async () => {
      if (!user || !tournamentId) {
        setAuthState({
          canManage: false,
          canEnterScores: false,
          userRole: null,
          loading: false,
          error: null,
        });
        return;
      }

      try {
        const [manageResult, scoresResult] = await Promise.all([
          canManageTournament(user.id, tournamentId),
          canEnterScores(user.id, tournamentId),
        ]);

        setAuthState({
          canManage: manageResult.canManage,
          canEnterScores: scoresResult.canEnterScores,
          userRole: manageResult.userRole || scoresResult.userRole,
          loading: false,
          error: manageResult.error || scoresResult.error,
        });
      } catch (error) {
        setAuthState({
          canManage: false,
          canEnterScores: false,
          userRole: null,
          loading: false,
          error: "Error checking permissions",
        });
      }
    };

    checkAuth();
  }, [user, tournamentId]);

  return authState;
};
